import { useEffect, useId, useState } from "react";
import { ThumbsUp, ThumbsDown, Star, Chat, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { endpoints, getUserId, adminToken } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";

/**
 * ValidationWidget — toggle voting for community_dataset or parameter targets.
 *  target_type: "community_dataset" | "parameter"
 */
export default function ValidationWidget({
    targetId,
    targetType = "community_dataset",
    initialAgree = 0,
    initialDisagree = 0,
    rating = null,
    compact = false,
    iconsOnly = false,
    discussionOnly = false,
    inlineReview = false,
    onCount,
}) {
    const canVote = Boolean(adminToken.get());
    const [agree, setAgree] = useState(initialAgree);
    const [disagree, setDisagree] = useState(initialDisagree);
    const [userVote, setUserVote] = useState(null); // "agree" | "disagree" | null
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const discussionId = useId();

    // Sync counts when target changes
    useEffect(() => {
        setAgree(initialAgree);
        setDisagree(initialDisagree);
    }, [initialAgree, initialDisagree, targetId]);

    // Load user's existing vote + comments
    useEffect(() => {
        const uid = getUserId();
        if (canVote) endpoints
            .myVote(targetId, targetType, uid)
            .then((r) => setUserVote(r.vote))
            .catch(() => setUserVote(null));
        if (!iconsOnly) endpoints
            .comments(targetId, targetType)
            .then(setComments)
            .catch(() => setComments([]));
    }, [targetId, targetType, canVote, iconsOnly]);

    const total = Math.max(agree + disagree, 1);
    const pct = Math.round((agree / total) * 100);

    const doVote = async (vote) => {
        try {
            setBusy(true);
            const res = await endpoints.toggleVote({
                target_id: targetId,
                target_type: targetType,
                user_id: getUserId(),
                vote,
            });
            setAgree(res.agree_count);
            setDisagree(res.disagree_count);
            setUserVote(res.user_vote);
            onCount?.(res.agree_count, res.disagree_count);
            const msg =
                res.action === "removed"
                    ? "Your vote was removed"
                    : res.action === "switched"
                    ? `Switched to ${vote}`
                    : vote === "agree"
                    ? "Endorsed"
                    : "Flagged as disputed";
            toast.success(msg);
        } catch {
            toast.error("Sign in as an administrator to review. You cannot review your own submission.");
        } finally {
            setBusy(false);
        }
    };

    const postComment = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        const c = await endpoints.addComment({
            target_id: targetId,
            target_type: targetType,
            author: "Guest Reviewer",
            author_role: "Contributor",
            text: text.trim(),
        });
        setComments((cs) => [c, ...cs]);
        setText("");
        toast.success("Comment posted");
    };

    if (iconsOnly) return <div className="sdx-summary-votes" aria-label="Community votes">
        {["agree", "disagree"].map(vote => <button key={vote} type="button" disabled={busy || !canVote}
            aria-label={`${vote === "agree" ? "Agree" : "Disagree"}: ${vote === "agree" ? agree : disagree} votes`}
            title={canVote ? vote === "agree" ? "Agree" : "Disagree" : "Sign in as an administrator to vote"}
            aria-pressed={userVote === vote} onClick={() => doVote(vote)}>
            {vote === "agree" ? <ThumbsUp size={17} /> : <ThumbsDown size={17} />}
            <span>{vote === "agree" ? agree : disagree}</span>
        </button>)}
    </div>;
    if (compact && !canVote) return <p className="text-xs text-slate-600">Counted reviews currently require an administrator account. Public reviewer accounts are not yet available.</p>;
    if (compact) return <div className="flex gap-2">
        {["agree", "disagree"].map(vote => <button key={vote} type="button" disabled={busy || !canVote}
            aria-pressed={userVote === vote} onClick={() => doVote(vote)}
            className={`inline-flex items-center justify-center gap-2 rounded border px-3 py-2.5 text-sm ${userVote === vote ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-300 text-slate-900 hover:border-violet-500"}`}>
            {vote === "agree" ? <ThumbsUp size={18} /> : <ThumbsDown size={18} />}
            {vote === "agree" ? "Agree" : "Disagree"}
        </button>)}
    </div>;

    return (
        <div
            data-testid={TEST_IDS.validationWidget}
            className={inlineReview ? "border-t border-violet-100 pt-4" : "border border-neutral-200 bg-white rounded-sm p-5"}
        >
            {!discussionOnly && <>
            <div className="label-caps flex items-center gap-2">
                <span className="w-6 h-px bg-[#6B46C1]" /> {inlineReview ? "Community review" : "Community Validation"}
            </div>

            {!inlineReview && <><div className="mt-4 flex items-baseline gap-3">
                <div className="font-serif text-5xl font-light">{pct}%</div>
                <div className="text-xs text-neutral-500">
                    agreement · {agree + disagree} votes
                </div>
            </div>

            <div className="mt-2 h-2 bg-neutral-100 relative border border-neutral-200">
                <div className="h-full bg-[#6B46C1]" style={{ width: `${pct}%` }} />
            </div></>}

            {userVote ? (
                <div
                    data-testid={TEST_IDS.userVoteIndicator}
                    className="mt-3 flex items-center gap-2 text-xs bg-[#F5F3FF] text-[#4C1D95] border border-[#DDD6FE] px-2 py-1.5 rounded-sm"
                >
                    <CheckCircle size={13} weight="fill" />
                    You voted <strong className="capitalize">{userVote}</strong> · click again to remove
                </div>
            ) : (
                <div className="mt-3 text-[11px] text-neutral-400">
                    {canVote ? "You have not voted yet" : "Sign in as an administrator to review. Guest votes are not counted."}
                </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                    data-testid={TEST_IDS.voteAgreeButton}
                    disabled={busy || !canVote}
                    onClick={() => doVote("agree")}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border text-sm font-medium transition-colors ${
                        userVote === "agree"
                            ? "bg-[#6B46C1] text-white border-[#6B46C1]"
                            : "bg-white border-neutral-300 hover:border-[#6B46C1]"
                    }`}
                    aria-pressed={userVote === "agree"}
                >
                    <ThumbsUp size={14} weight={userVote === "agree" ? "fill" : "regular"} />
                    <span data-testid={TEST_IDS.voteAgreeCount}>Agree {agree}</span>
                </button>
                <button
                    data-testid={TEST_IDS.voteDisagreeButton}
                    disabled={busy || !canVote}
                    onClick={() => doVote("disagree")}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border text-sm font-medium transition-colors ${
                        userVote === "disagree"
                            ? "bg-[#09090B] text-white border-black"
                            : "bg-white border-neutral-300 hover:border-black"
                    }`}
                    aria-pressed={userVote === "disagree"}
                >
                    <ThumbsDown size={14} weight={userVote === "disagree" ? "fill" : "regular"} />
                    <span data-testid={TEST_IDS.voteDisagreeCount}>Disagree {disagree}</span>
                </button>
                {inlineReview && <button type="button" aria-expanded={reviewOpen} aria-controls={discussionId} onClick={() => setReviewOpen(open => !open)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-violet-300 text-violet-700 text-sm font-medium hover:bg-violet-50"><Chat size={14} /> Review ({comments.length})</button>}
            </div>

            {rating != null && <div className="mt-3 flex items-center gap-2 text-sm">
                <div className="flex items-center gap-0.5 text-[#EAB308]">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                            key={i}
                            size={14}
                            weight={i <= Math.round(rating) ? "fill" : "regular"}
                        />
                    ))}
                </div>
                <span className="font-mono text-xs">{rating.toFixed(1)} / 5.0</span>
            </div>}

            </>}
            <div id={discussionId} hidden={inlineReview && !reviewOpen} className={discussionOnly ? "" : "mt-5"}>
                <div className="label-caps flex items-center gap-2 mb-3">
                    <Chat size={12} /> Discussion · {comments.length}
                </div>
                <form onSubmit={postComment} className="mb-3 flex gap-2">
                    <input
                        aria-label="Review note"
                        data-testid={TEST_IDS.commentInput}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Add a review note…"
                        className="flex-1 px-3 py-2 border border-neutral-200 text-sm focus:outline-none focus:border-[#6B46C1] rounded-sm"
                    />
                    <button
                        aria-label="Post review note"
                        data-testid={TEST_IDS.commentSubmit}
                        type="submit"
                        className="btn-primary px-3 py-2"
                    >
                        <PaperPlaneRight size={14} weight="fill" />
                    </button>
                </form>
                <div
                    data-testid={TEST_IDS.commentList}
                    className="space-y-3 max-h-64 overflow-y-auto pr-2"
                >
                    {comments.map((c) => (
                        <div key={c.id} className="border-l-2 border-[#E9D8FD] pl-3">
                            <div className="text-xs">
                                <span className="font-semibold">{c.author}</span>
                                <span className="ml-2 text-[10px] label-caps bg-[#FFD60A]/30 border border-[#EAB308] px-1 py-0.5 rounded-sm">
                                    {c.author_role}
                                </span>
                            </div>
                            <div className="text-sm mt-1 text-neutral-700">{c.text}</div>
                            <div className="text-[10px] text-neutral-400 font-mono mt-1">
                                {new Date(c.created_at).toLocaleDateString()}
                            </div>
                        </div>
                    ))}
                    {comments.length === 0 && (
                        <div className="text-xs text-neutral-400 italic">
                            No comments yet — be the first to review.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
